//Generated by Copilot- Start
import ChannelLineUpProvider, { KeyValuePair } from "@directv-digital/dds.services.channel-lineup-provider";
import {
  attachAnalytics,
  buildSvodPackageLists,
  buildCategoryLookup,
  buildPackageSummaries,
  filterCategories,
  enhanceCategoriesWithSvodLabels,
  groupChannelsByGenrePackage,
  groupChannelsByCategory,
  partitionChannelsByVisibility,
  buildRsnModalData,
  buildAddOnChannelsByCategory,
  extractCmsData,
  parseChannelsForV2,
  handleOpenChannelModal,
  handleOpenSvodModal,
  resolveTheme,
  buildMyFreeDtvObject,
  buildGenreAddonChannelSet,
  normalizeChannelData,
  buildSearchIndex
} from "../../helper/channel-interactive-tool-v2";
import { extractGenreTooltips } from "../../helper/genre-helper";
import { bucketChannelsByCategory, structuralCategoryKey, orderChannelCategoryConfig, pickChannels, pickChrome, isUsingServiceChannels, applyEntertainmentCategoryFallback } from "../../helper/channels-service-helpers";
import { ComponentNames } from "../../constant/component-constant";
import { logAdapterEvent, logError } from "../../helper/logger";
import { addPackageDetailsToEmbeddedSvods } from "../../helper/svod-utils";
import { cleanEmpty, arraytoObj } from "../../helper/sb-fixer";
import { sharedChannelCache } from "../../helper/channel-interactive-tool-v2/channel-cache";

const ADAPTER_NAME = "channelsInteractiveToolAdapterV2";

export const channelsInteractiveToolAdapterV2 = (args: any) => {
  try {
    const {
      content,
      chnlListinPageData,
      index,
      myFreeDTVConfig,
      displayGenrePackSatellite,
      displayGenrePack,
      genreSvodOffers,
      genreConfig,
      ovChnlPageData,
      packageConfig,
      cmsPageName,
      embeddedSvods,
      dtviEmbeddedIncludedSvods,
      dtvsEmbeddedIncludedSvods,
      dtviSvodOffers,
      dtvsSvodOffers,
      svodDtviPcp,
      svodDtvsPcp,
      rsnTierLegalTextObj,
      genreLocalChannelMapping,
      signatureExcludedLocalChannels,
      svodGenreAddon,
      svodGenreData,
      genreNationals,
      channelsServiceData,
      globalLineupConfig,
    } = args;
    logAdapterEvent(ADAPTER_NAME, "starting V2 adaptation", {
      cmsPageName,
      index,
      chnlSrc: content?.chnlSrc,
      hasChannelData: Boolean(chnlListinPageData),
      showMyFreeDTVChannels: content?.showMyFreeDTVChannels,
      singlePackageView: content?.config?.singlePackageView
    });

    // Extract CMS data
    const cmsData = extractCmsData(chnlListinPageData);
    if (!cmsData) {
      logError(ADAPTER_NAME, new Error("No CMS data available"), {
        cmsPageName,
        index
      });
      return null;
    }

    // Determine flags
    const chnlSrc = content?.chnlSrc || "streamclup";
    const isSatellite = chnlSrc === "dtvClup";

    // Parse config early (needed for packageSummaries)
    const config = Array.isArray(content?.config) || typeof content?.config === "object" ?
      cleanEmpty(arraytoObj(content?.config)) :
      {};

    // Determine if genre packs should be displayed based on source
    const resolvedDisplayGenrePack = chnlSrc === "dtvClup" ?
      displayGenrePackSatellite :
      displayGenrePack;

    const svodMapping = genreSvodOffers?.svodMapping;

    // Build package summaries from packageConfig (single source of truth)
    // Handles all variant-specific filtering in one pass
    const packageSummaries = buildPackageSummaries({
      packageConfig: packageConfig || [],
      chnlSrc,
      displayGenrePack: resolvedDisplayGenrePack,
      displayGenrePackSatellite,
      singlePackageView: config?.singlePackageView,
      singlePackageConfig: config?.singlePackageConfig,
      showGenrePackages: content?.showGenrePackages
    });

    // Flag-ON: cmsData.channelCategories arrives as the package-config
    // {label, engCategories[]} objects. Mirror the lineup — KV display list
    // (key=value=label) for accordion headers; bucket channels by
    // engCategories ↔ channel.categories[] (multi-membership) via bucket-channels.
    // Channels + chrome are picked by the channel-service flag: flag-ON ->
    // service / package-config, flag-OFF -> AEM (cmsData = AEM listing).
    let sourceChannels = pickChannels(channelsServiceData?.channels, cmsData.channels);
    // Category config follows the SAME source as the channels (pickChannels), so config and
    // channels always share an origin. Two shapes — handled on two paths (WAT-11076):
    //
    //  - SERVICE (flag-ON + non-empty feed): globalLineupConfig.categoryConfig — an ARRAY of
    //    {label, engCategories[]}. Channels carry prose categories[]; the visible grid buckets by
    //    categories[] ∩ engCategories (bucketChannelsByCategory). Zip/DMA rows (Local/RSN) lead,
    //    so KV uses the structural key; content rows keep label===key.
    //
    //  - AEM FALLBACK (flag-OFF, or flag-ON but the service is empty/down/404): AEM ships
    //    channelCategories as an OBJECT { options: ["Label,KEY", …] } and channels carry only a
    //    `categoryId` slug (no categories[]). Parse the options CSV via getKVsFromStrings and bucket
    //    visible channels by categoryId (groupChannelsByCategory). This RESTORES the pre-migration
    //    behavior that commit 35070b8b3 dropped — it replaced the `.options` unwrap with
    //    `Array.isArray(channelCategories) ? … : []`, which yields [] for the AEM object shape and
    //    blanked the categorical grid + banner on every AEM/degraded render (e.g. test2: service 404).
    const usingServiceChannels = isUsingServiceChannels(channelsServiceData?.channels);
    const serviceCategoryConfig = Array.isArray(globalLineupConfig?.categoryConfig)
      ? globalLineupConfig.categoryConfig
      : [];
    // Service path only: ordered package-config (engCategories) for bucketChannelsByCategory.
    // AEM path leaves this empty — bucketing there is categoryId-based via groupChannelsByCategory.
    const orderedCategoryConfig = usingServiceChannels ?
      orderChannelCategoryConfig(serviceCategoryConfig) :
      [];
    const channelCategoriesKV: KeyValuePair[] = usingServiceChannels ?
      orderedCategoryConfig.map((c: any) => {
        const structuralKey = structuralCategoryKey(c);
        return structuralKey ?
          { key: structuralKey, value: c.label } :
          { key: c.label, value: c.label };
      }) :
      (ChannelLineUpProvider.getKVsFromStrings(cmsData.channelCategories?.options) || []);
    const categoryLookup = buildCategoryLookup(channelCategoriesKV);

    // Build genre addon channel set for V2
    const svodGenrePkgList = genreSvodOffers?.svodGenrePkgList;
    // svodGenreAddon is now passed as a separate parameter from page-adapter.ts
    const genreAddonChannelSet = buildGenreAddonChannelSet(svodGenrePkgList, svodGenreAddon);

    // Determine variant flags for channel filtering
    const isGenreVariant = Boolean(content?.showGenrePackages);
    const isSinglePackageVariant = Boolean(config?.singlePackageView);

    // Compute modal flags early — needed to derive stripPackageRefs before parsing channels
    const openChannelModal = handleOpenChannelModal({
      singlePackageView: config?.singlePackageView,
      singlePackageConfig: config?.singlePackageConfig,
      showMyFreeDTVChannels: content?.showMyFreeDTVChannels,
      displayGenrePack: content?.showGenrePackages
    });

    const openSvodModal = handleOpenSvodModal({
      singlePackageView: config?.singlePackageView,
      singlePackageConfig: config?.singlePackageConfig,
      showMyFreeDTVChannels: content?.showMyFreeDTVChannels,
      displayGenrePack: content?.showGenrePackages
    });

    // Strip signaturePackages/genrePackages from channel cards when both modals are disabled.
    // Those arrays are only consumed by the channel modal — if neither modal can open,
    // sending them to the client is pure wasted bytes.
    const stripPackageRefs = !openChannelModal && !openSvodModal;

    // Entertainment category fallback (standard variant only) — mirrors the lineup
    // (chlupStore.ts applyEntertainmentCategoryFallback). Some included service
    // channels carry only FORMAT categories (e.g. ["4K","Mixed Authorization"]) that
    // match no configured content accordion, so resolveChannelCategoryKey returns an
    // empty slug and bucketChannelsByCategory drops them from the categorical view.
    // Mutating the RAW sourceChannels' categories[] BEFORE parse fixes both consumers
    // at once: parseChannelsForV2 copies categories[] onto each card (so
    // resolveChannelCategoryKey now matches Entertainment) and bucketChannelsByCategory
    // (partition.visible) buckets under Entertainment via the same categories[].
    // Raw service channels have no isInclAny, so the include gate here is "included in
    // a base package" (packageListSystem) and not MyFree; structural Local/RSN and
    // already-categorized channels are skipped inside the helper. Skipped for the genre
    // variant, which buckets by pack membership (groupChannelsByGenrePackage), not category.
    if (!isGenreVariant) {
      // Clone-on-write: returns a new array (the input may be the frozen channels-service
      // cache — WAT-11076). Reassign so parseChannelsForV2 below reads the fallback-applied
      // channels. Genre variant skips this and reads sourceChannels read-only.
      sourceChannels = applyEntertainmentCategoryFallback(
        sourceChannels,
        orderedCategoryConfig,
        (c: any) => Boolean(c?.packageListSystem?.length) && !c?.myFree
      );
    }

    const { channels: parsedChannels, svodPromoMap } = parseChannelsForV2({
      channels: sourceChannels,
      packageSummaries,
      chnlSrc,
      categoryLookup,
      // Ordered package-config category config — lets parseChannelsForV2 resolve
      // each channel's single representative categoryId from its categories[] ∩
      // engCategories (flag-ON), landing on the same bucket key the visible grid
      // uses. Flag-OFF (no engCategories) leaves the legacy categoryId slug intact.
      categoryConfig: orderedCategoryConfig,
      myFreeDTVConfig,
      displayGenrePack: resolvedDisplayGenrePack,
      showMyFreeDTVChannels: content?.showMyFreeDTVChannels,
      isGenreVariant,
      isSinglePackageVariant,
      singlePackageConfig: config?.singlePackageConfig,
      genreConfig,
      svodDetails: isSatellite ? dtvsSvodOffers : dtviSvodOffers,
      svodPcpDetails: isSatellite ? svodDtvsPcp : svodDtviPcp,
      genreAddonChannelSet,
      packageConfig,
      stripPackageRefs
    });

    // Build genre categories from packageConfig (not CMS)
    // For genre variant, categories ARE the genre packages themselves
    // Filter packageConfig by type: "genre" and "genre-mini"
    // Sort by order property (ascending)
    // Map to KeyValuePair format: { key: id, value: displayLabel }
    // Keys are full IDs like ATT_TV_MYSPORTS (no more prepending ATT_TV_)
    const genreCategories: KeyValuePair[] = isGenreVariant ?
      (packageConfig || [])
        .filter((pkg: any) => pkg.type === "genre" || pkg.type === "genre-mini")
        .sort((a: any, b: any) => {
          const orderA = parseInt(a.order, 10) || 0;
          const orderB = parseInt(b.order, 10) || 0;
          return orderA - orderB;
        })
        .map((pkg: any) => ({
          key: pkg.id,           // e.g., "ATT_TV_MYSPORTS"
          value: pkg.displayLabel // e.g., "MySports"
        })) :
      [];

    // Partition channels
    // In genre variant ALL parsed channels are "visible" — partitioning by hasIncludedPackages
    // is a signature-variant concept. Genre channels should all appear in their category buckets.
    const partition = isGenreVariant ?
      {
        visible: parsedChannels,
        hiddenByCategory: {}
      } :
      partitionChannelsByVisibility({
        channels: parsedChannels,
        categoryLookup
      });

    // Group channels by their category
    // - Standard variant: group by channel.categoryId (SPORTS, ENTERTAINMENT…)
    // - Genre variant:    reads raw CMS channel.packageListSystem directly (not stripped card refs)
    //                     same channel can appear in multiple genre categories
    //                     now uses full package IDs (ATT_TV_MYSPORTS) directly from packageConfig
    // Service: prose categories[] ∩ engCategories (multi-membership). AEM fallback: bucket by
    // the channel's categoryId slug (pre-35070b8b3 behavior). Genre: by pack membership.
    const channelsByCategory = isGenreVariant ?
      groupChannelsByGenrePackage(sourceChannels, parsedChannels, genreCategories) :
      usingServiceChannels ?
        bucketChannelsByCategory(partition.visible, orderedCategoryConfig) :
        groupChannelsByCategory(partition.visible, categoryLookup);

    // Build flat arrays for client-side usage (eliminates duplicate category groupings)
    // 1. All hidden channels (flat array, sorted alphabetically) — empty in genre variant
    const allHiddenChannels = Object.values(partition.hiddenByCategory)
      .flatMap(group => group.channels)
      .sort((a, b) => {
        const nameA = (a.label || "").toLowerCase();
        const nameB = (b.label || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });

    // 2. Add-on channels — not applicable in genre variant (all channels are "visible")
    const addOnChannelsByCategory = isGenreVariant ?
      {} :
      buildAddOnChannelsByCategory(partition.hiddenByCategory);
    const allAddOnChannels = Object.values(addOnChannelsByCategory).flat();

    // 3. All channels for search — omitted entirely in genre variant (search is disabled)
    const allChannels = isGenreVariant ? [] : [...partition.visible, ...allHiddenChannels];

    // Normalize channel data - create channelMap and convert arrays to string references
    // This eliminates duplication: each channel stored once in channelMap,
    // all other structures reference by channel name (58-60% payload reduction)
    // Note: LOCAL/RSN channels excluded as they're fetched dynamically per zipcode
    const normalizedData = normalizeChannelData({
      channelsByCategory,
      addOnChannelsByCategory,
      allChannels,
      allHiddenChannels,
      allAddOnChannels
    });

    // Build banner categories:
    // - Genre variant:    use packageConfig-derived categories (ATT_TV_MYSPORTS, etc.),
    //                     already sorted by packageConfig.order in genreCategories construction,
    //                     filtered to only categories that have at least one channel,
    //                     enhanced with SVOD labels if available
    // - Standard variant: use CMS channelCategories (SPORTS, ENTERTAINMENT…),
    //                     strip ENTERTAINMENT_AND_MUSIC, enhanced with SVOD labels if genre mode
    let bannerCategories: KeyValuePair[];
    if (isGenreVariant) {
      // Filter to categories with channels, then enhance with SVOD labels
      const categoriesWithChannels = genreCategories
        .filter(cat => (normalizedData.channelsByCategory[cat.key]?.length ?? 0) > 0);

      // Enhance with SVOD labels if available
      bannerCategories = (svodMapping && svodGenreData) ?
        enhanceCategoriesWithSvodLabels({
          categories: categoriesWithChannels,
          svodGenreData,
          svodMapping
        }) :
        categoriesWithChannels;
    } else {
      bannerCategories = filterCategories(
        channelCategoriesKV,
        content?.suppressCategory
      ).filter(cat => cat.key !== "ENTERTAINMENT_AND_MUSIC");

      // Enhance standard categories with SVOD labels if available
      if (svodMapping && svodGenreData) {
        bannerCategories = enhanceCategoriesWithSvodLabels({
          categories: bannerCategories,
          svodGenreData,
          svodMapping
        });
      }
    }

    // Generated by Copilot- Start - 2026-04-01 17:20:58
    // Rename the SPORTS category label to "National Sports" for all variants except
    // the MyFree single-package view (singlePackageView=true + packageId="MYFREE_DTV").
    const isMyFreeSinglePackage =
      isSinglePackageVariant &&
      config?.singlePackageConfig?.packageId === "MYFREE_DTV";

    if (!isMyFreeSinglePackage) {
      bannerCategories = bannerCategories.map(cat => (cat.key === "SPORTS" ? {
        ...cat,
        value: "National Sports"
      } : cat));
    }
    // Generated by Copilot- End - 2026-04-01 17:20:58

    // Build SVOD section with V2 package lists
    const updatedEmbeddedSvods = addPackageDetailsToEmbeddedSvods({
      embeddedSvods,
      epochSvodsData: isSatellite ? dtvsEmbeddedIncludedSvods : dtviEmbeddedIncludedSvods,
      packageConfig,
      componentType: ComponentNames.CHLUP_INTERACTIVE_TOOL,
      svodMapping,
      isSatellite,
      singlePackageView: config?.singlePackageView,
      singlePackageConfig: config?.singlePackageConfig
    });

    // Build SVOD tiles with package lists for V2
    // updatedEmbeddedSvods has packageListSystem + genrePacks from addPackageDetailsToEmbeddedSvods
    // buildSvodPackageLists converts these into signaturePackageList + genrePackageList
    // which is the shape expected by SvodBannerV2 / SvodModalV2
    const svodTilesV2 = (updatedEmbeddedSvods || [])
      .map((svod: any) => {
        const { signaturePackageList, genrePackageList } = buildSvodPackageLists({
          packageListSystem: svod.packageListSystem || [],
          genrePacks: svod.genrePacks || [],
          packageSummaries,
          chnlSrc,
          genreConfig
        });
        return {
          id: svod.offerCode || svod.name,
          name: svod.name,
          displayText: svod.name,
          icon: svod.interactiveToolData?.icon || svod.icon,
          signaturePackageList,
          genrePackageList,
          svodData: true,
          // Add deviceIncludedText for signature package legal text (used in modal)
          ...(svod.interactiveToolData?.deviceIncludedText && {
            deviceIncludedText: svod.interactiveToolData.deviceIncludedText
          })
        };
      })
      // Drop SVOD tiles where both package lists are empty — they would render an
      // orphan modal with no packages to upsell (e.g. AMC+ in satellite view).
      .filter((tile: any) =>
        tile.signaturePackageList.length > 0 || tile.genrePackageList.length > 0
      );

    // Build RSN modal data
    const rsnModalData = buildRsnModalData(
      parsedChannels.filter(channel => Boolean(channel.isRsn || channel.isLocal))
    );

    // Build search index
    const searchIndex = buildSearchIndex(
      partition.visible,
      partition.hiddenByCategory
    );

    // Resolve analytics
    const resolvedAnalytics = attachAnalytics({
      content,
      index
    });

    // Resolve theme
    const resolvedTheme = resolveTheme(content?.theme, ADAPTER_NAME, index);

    // Build myFreeDTVObj using helper function (matches V1 logic)
    const rawMyFreeDTVObj = buildMyFreeDtvObject(content?.myFreeDTVObj, packageConfig);
    // Normalize for V2 modal (array_package → package, cta array → cta object)
    const normalizedMyFreeDTVObj = cleanEmpty(arraytoObj(rawMyFreeDTVObj));

    // Add packageLegal to each package item after normalization
    const myFreeDTVObj = {
      ...normalizedMyFreeDTVObj,
      package: normalizedMyFreeDTVObj?.package?.map((pkg: any) => ({
        ...pkg,
        packageLegal: pkg.packageName === "Genre Packs" ?
          (pkg.packageLegal || "Included in all packs") :
          (pkg.packageLegal || "Included in all packages")
      }))
    };

    // Extract genre-related data for tooltips
    const tooltips = {
      genreTooltips: extractGenreTooltips(pickChrome(globalLineupConfig?.genrePackages, cmsData.genrePackages) || [])
    };

    // Extract zipCodeModule and myHomeTeamModalCta from content
    const zipCodeModule = content?.zipcodeModuleConfig || null;
    const myHomeTeamModalCta = cmsData.myHomeTeamModalCta || null;
    const genreEnabled = Boolean(resolvedDisplayGenrePack);

    // Determine if zipcode module should be enabled
    const enableZipcodeModule = Boolean(
      !content?.showMyFreeDTVChannels &&
      (!config?.singlePackageView || config?.singlePackageConfig?.showZipCodeModule)
    );

    // Determine if search should be enabled (default true, hidden when single package view without enableSearch)
    const enableSearch = config?.singlePackageView ?
      Boolean(config?.singlePackageConfig?.enableSearch) :
      true;

    // Determine if radio button should be enabled (same logic as enableSearch)
    const enableRadioButton = config?.singlePackageView ?
      Boolean(config?.singlePackageConfig?.enableRadioButton) :
      true;

    // Parse searchObj from content (same as V1)
    const searchObj = content?.searchObj ? cleanEmpty(arraytoObj(content.searchObj)) : {};

    // Return complete props
    return {
      props: {
        ...content,
        searchObj,
        displayGenrePack,
        displayGenrePackSatellite,
        config: {
          ...config,
          enableZipcodeModule,
          enableSearch,
          enableRadioButton,
          openChannelModal,
          openSvodModal
        },
        theme: resolvedTheme,
        analytics: resolvedAnalytics,
        heading: content.heading?.[0] || content.heading,
        subheading: content?.subheading?.[0],
        isSatellite,
        chnlSrc,
        genreEnabled,
        zipCodeModule,
        myHomeTeamModalCta,
        tooltips,
        rsnTierLegalTextObj,
        genreLocalChannelMapping,
        signatureExcludedLocalChannels,
        genreNationals,
        options: {
          id: content.id
        },
        // Generated by Copilot- Start - 2026-03-30 16:59:31
        // ALL variants strip derived channel structures from the SSR payload and lazy-load
        // them client-side via /api/channel-data.
        //
        // Optimization (vs previous approach): instead of caching raw CMS channels and
        // re-running the full parsing pipeline on the client, we now cache the fully
        // normalized output (normalizedData) under a variant-specific compound key.
        // The API serves the pre-parsed blob directly — zero client-side parsing.
        //
        // Cache key format: "<chnlSrc>:<variant>"
        //   - "streamclup:internet"          → internet variant
        //   - "streamclup:genre"             → genre variant
        //   - "streamclup:singlepackage:<id>"→ single-package variant (packageId disambiguates)
        //   - "dtvclup:satellite"            → satellite variant
        //
        // The client reads chlupV2.cacheVariant to know which compound key to request.
        chlupV2: (() => {
          // Build the variant suffix for the cache key.
          // Single-package views include the packageId to avoid collisions between
          // different CMS blocks that share the same chnlSrc but filter to different packages.
          const srcKey = (chnlSrc || "default").toLowerCase();
          const cacheVariant: string = isGenreVariant ?
            `${srcKey}:genre` :
            isSinglePackageVariant ?
              `${srcKey}:singlepackage:${(config?.singlePackageConfig?.packageId || "default").toLowerCase()}` :
              chnlSrc === "dtvClup" ?
                `${srcKey}:satellite` :
                `${srcKey}:internet`;

          // Write the already-computed normalizedData into the shared cache under the
          // variant-specific key.  The /api/channel-data route serves entry.serialized
          // verbatim, so the client receives a NormalizedChannelData blob and can call
          // setLazyChannelData(data) directly — no parsing required.
          sharedChannelCache[cacheVariant] = {
            data: normalizedData,
            serialized: JSON.stringify(normalizedData)
          };

          return {
            bannerCategories,
            // All variants strip the expensive derived channel structures from SSR.
            // The client fills these in by fetching /api/channel-data?source=…&variant=…
            channelMap: {},
            channelsByCategory: {},
            ...(!isGenreVariant ? {
              addOnChannelsByCategory: {},
              allChannels: [],
              allHiddenChannels: [],
              allAddOnChannels: []
            } : {}),
            allCategories: isMyFreeSinglePackage
              ? channelCategoriesKV
              : channelCategoriesKV.map(cat => cat.key === "SPORTS" ? { ...cat, value: "National Sports" } : cat),
            packageSummaries,
            svodPromoMap,
            ovChnlPageData,
            svodTiles: svodTilesV2,
            rsnModalData,
            myFreeDTVObj,
            channelsLazyLoaded: true,
            // Tell the client exactly which compound cache key to request.
            // Without this the client would have to guess the variant suffix.
            cacheVariant
          };
        })()
        // Generated by Copilot- End - 2026-03-30 16:59:31
      },
      type: ComponentNames.CHLUP_INTERACTIVE_TOOL
    };
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      cmsPageName: args.cmsPageName,
      index: args.index
    });
    return null;
  }
};
// Generated by Copilot- End
